import katex from "katex";
import "katex/dist/katex.min.css";

// Renders a string that may contain LaTeX math, typeset via KaTeX.
// Supported delimiters: \( ... \) and $ ... $ (inline), \[ ... \] and $$ ... $$ (display).
// Non-math text is rendered verbatim; malformed math falls back to raw text.
const SEGMENT_RE = /(\\\[[\s\S]+?\\\]|\$\$[\s\S]+?\$\$|\\\([\s\S]+?\\\)|\$[^$\n]+?\$)/g;

function renderMath(expr, displayMode) {
  try {
    return katex.renderToString(expr, { displayMode, throwOnError: false, strict: false });
  } catch {
    return null;
  }
}

// Split a source string into ordered segments of plain text and typeset math.
// Returns: [{ t: "text", v } | { t: "math", v: <html>, display, raw }]
export function splitMath(src) {
  const s = src == null ? "" : String(src);
  if (!s) return [];
  if (!/[\\$]/.test(s)) return [{ t: "text", v: s }];

  const parts = [];
  let last = 0;
  let m;
  SEGMENT_RE.lastIndex = 0;
  while ((m = SEGMENT_RE.exec(s)) !== null) {
    if (m.index > last) parts.push({ t: "text", v: s.slice(last, m.index) });
    const raw = m[0];
    const display = raw.startsWith("\\[") || raw.startsWith("$$");
    let inner;
    if (raw.startsWith("\\[") || raw.startsWith("\\(")) inner = raw.slice(2, -2);
    else if (raw.startsWith("$$")) inner = raw.slice(2, -2);
    else inner = raw.slice(1, -1);
    const html = renderMath(inner.trim(), display);
    parts.push(html ? { t: "math", v: html, display, raw } : { t: "text", v: raw });
    last = m.index + raw.length;
  }
  if (last < s.length) parts.push({ t: "text", v: s.slice(last) });
  return parts;
}

// Returns true when the string contains any renderable math delimiters.
export function hasMath(src) {
  return splitMath(src).some((p) => p.t === "math");
}

// Some models emit bare LaTeX (e.g. "\frac{6}{8}") for short answers/MCQ
// options without \( ... \) delimiters. When a string has no delimiters but
// clearly contains LaTeX macros, wrap the whole thing so it typesets.
const BARE_LATEX_RE = /\\(frac|dfrac|sqrt|times|div|pm|mp|cdot|leq|geq|neq|approx|pi|theta|alpha|beta|Delta|degree|infty)\b|[\^_]\{|\^[0-9A-Za-z]|_[0-9A-Za-z]/;
export function wrapBareMath(src) {
  const s = src == null ? "" : String(src);
  if (!s || /\\\(|\\\[|\$/.test(s)) return s;
  return BARE_LATEX_RE.test(s) ? `\\(${s}\\)` : s;
}

// Build an HTML string (for injection into html2canvas / print DOM) where plain
// text is escaped via `esc` and math segments are typeset by KaTeX.
export function mathToHtml(src, esc) {
  const escape = esc || ((t) => String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"));
  return splitMath(src)
    .map((p) => (p.t === "math" ? p.v : escape(p.v)))
    .join("");
}

// ── PDF / html2canvas-safe math renderer ─────────────────────────────
// KaTeX relies on precise negative-margin vlist positioning + web-fonts that
// html2canvas rasterises poorly (thin numerators overlap the fraction bar and
// look like "+"). For PDF export we therefore build plain block/border HTML
// that html2canvas renders reliably. Screen rendering still uses KaTeX.
const _SYM = {
  times: "\u00d7", div: "\u00f7", pm: "\u00b1", mp: "\u2213", cdot: "\u00b7",
  ast: "\u2217", star: "\u2605", leq: "\u2264", le: "\u2264", geq: "\u2265",
  ge: "\u2265", neq: "\u2260", ne: "\u2260", approx: "\u2248", equiv: "\u2261",
  infty: "\u221e", ldots: "\u2026", cdots: "\u22ef", dots: "\u2026",
  pi: "\u03c0", theta: "\u03b8", alpha: "\u03b1", beta: "\u03b2",
  gamma: "\u03b3", lambda: "\u03bb", mu: "\u03bc", Delta: "\u0394",
  Sigma: "\u03a3", Omega: "\u03a9", degree: "\u00b0", circ: "\u00b0",
  percent: "%", rightarrow: "\u2192", to: "\u2192", leftarrow: "\u2190",
  angle: "\u2220", triangle: "\u25b3", square: "\u25a1", therefore: "\u2234",
  because: "\u2235", Rightarrow: "\u21d2", Leftrightarrow: "\u21d4",
  parallel: "\u2225", perp: "\u22a5", cong: "\u2245", sim: "\u223c",
  propto: "\u221d", cup: "\u222a", cap: "\u2229", in: "\u2208",
  notin: "\u2209", subset: "\u2282", supset: "\u2283", forall: "\u2200",
  exists: "\u2203", sum: "\u2211", prod: "\u220f", int: "\u222b",
  partial: "\u2202", nabla: "\u2207", prime: "\u2032", bullet: "\u2022",
  overline: "", bar: "", vec: "",
  left: "", right: "", quad: " ", qquad: "  ", displaystyle: "", ",": " ",
  ";": " ", ":": " ", "!": "",
};

function _latexToPdfHtml(expr, esc) {
  const s = String(expr ?? "");
  const n = s.length;
  let i = 0;
  let out = "";
  const readGroup = () => {
    let depth = 0;
    const start = i;
    for (; i < n; i++) {
      if (s[i] === "{") depth++;
      else if (s[i] === "}") { depth--; if (depth === 0) { const g = s.slice(start + 1, i); i++; return g; } }
    }
    return s.slice(start + 1);
  };
  const readArg = () => {
    while (s[i] === " ") i++;
    if (s[i] === "{") return readGroup();
    if (s[i] === "\\") { let j = i + 1; while (j < n && /[a-zA-Z]/.test(s[j])) j++; const t = s.slice(i, j); i = j; return t; }
    const c = s[i] ?? ""; i++; return c;
  };
  while (i < n) {
    const c = s[i];
    if (c === "\\") {
      let j = i + 1;
      while (j < n && /[a-zA-Z]/.test(s[j])) j++;
      const cmd = s.slice(i + 1, j);
      i = j;
      if (cmd === "frac" || cmd === "dfrac" || cmd === "tfrac") {
        const a = readArg(); const b = readArg();
        const na = _latexToPdfHtml(a, esc); const nb = _latexToPdfHtml(b, esc);
        // Inline slash form (1/2, 4/3). Parenthesise compound parts.
        const wrapA = /[\s+\-*/=<>]/.test(a.trim()) ? `(${na})` : na;
        const wrapB = /[\s+\-*/=<>]/.test(b.trim()) ? `(${nb})` : nb;
        out += `${wrapA}/${wrapB}`;
      } else if (cmd === "sqrt") {
        const a = readArg();
        out += `<span class="msqrt">&radic;<span class="msqrt-in">${_latexToPdfHtml(a, esc)}</span></span>`;
      } else if (cmd === "text" || cmd === "mathrm" || cmd === "mathbf" || cmd === "operatorname") {
        out += _latexToPdfHtml(readArg(), esc);
      } else if (Object.prototype.hasOwnProperty.call(_SYM, cmd)) {
        out += _SYM[cmd];
      } else if (cmd === "") {
        const nc = s[i]; i++;
        if (nc === "{" || nc === "}" || nc === "%" || nc === "$" || nc === "&" || nc === "#" || nc === "_") out += esc(nc);
        else out += " ";
      }
      // unknown multi-letter command → dropped
    } else if (c === "^") {
      i++; out += `<sup>${_latexToPdfHtml(readArg(), esc)}</sup>`;
    } else if (c === "_") {
      i++; out += `<sub>${_latexToPdfHtml(readArg(), esc)}</sub>`;
    } else if (c === "{" || c === "}") {
      i++;
    } else {
      out += esc(c); i++;
    }
  }
  return out;
}

// Build html2canvas-safe HTML: inline slash fractions (a/b), superscript
// powers (3²), roots and symbols. Runs the whole string through the parser so
// stray/undelimited LaTeX (e.g. "3^2", "\frac{6}{8}") is normalised too.
export function mathToHtmlPdf(src, esc) {
  const escape = esc || ((t) => String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"));
  let s = src == null ? "" : String(src);
  if (!s) return "";
  if (!/[\\$^_{}]/.test(s)) return escape(s);
  // Strip math delimiters, then normalise the entire string.
  s = s.replace(/\\\[|\\\]|\\\(|\\\)/g, " ").replace(/\$\$?/g, " ");
  return _latexToPdfHtml(s, escape).replace(/[ \t]{2,}/g, " ");
}

// Sanitise a single AI-generated <svg> block for safe HTML injection.
export function sanitizeSvg(svg) {
  const s0 = svg == null ? "" : String(svg);
  if (!/<svg/i.test(s0)) return "";
  const m = s0.match(/<svg[\s\S]*?<\/svg>/i);
  if (!m) return "";
  let s = m[0].slice(0, 24000);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, "");
  s = s.replace(/<style[\s\S]*?<\/style>/gi, "");
  s = s.replace(/<\/?(?:script|foreignObject|image|iframe|use|a)\b[^>]*>/gi, "");
  s = s.replace(/\son[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  s = s.replace(/\s(?:xlink:)?href\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  s = s.replace(/javascript:/gi, "");
  return s.trim();
}

// Pull sanitized <svg> figures out of a string; returns { text, svgs }.
export function extractSvgs(src) {
  const s0 = src == null ? "" : String(src);
  if (!/<svg/i.test(s0)) return { text: s0, svgs: [] };
  const svgs = [];
  // Some models wrap the SVG in a markdown code fence (```svg ... ```); drop
  // those fence markers so they don't leak into the visible reply.
  let s = s0.replace(/```+[ \t]*[a-zA-Z0-9]*/g, " ");
  s = s.replace(/<svg[\s\S]*?<\/svg>/gi, (m) => {
    const c = sanitizeSvg(m);
    if (c) svgs.push(c);
    return " ";
  });
  return { text: s, svgs };
}

export function MathText({ text, className }) {
  const parts = splitMath(text);
  if (!parts.length) return <span className={className} />;
  return (
    <span className={className}>
      {parts.map((p, idx) =>
        p.t === "math" ? (
          <span key={idx} dangerouslySetInnerHTML={{ __html: p.v }} />
        ) : (
          <span key={idx}>{p.v}</span>
        )
      )}
    </span>
  );
}

// Inline renderer matching the PDF: slash fractions (a/b) + superscript powers.
export function MathInline({ text, className }) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: mathToHtmlPdf(text) }} />;
}

export default MathText;
